import { supabase } from "@/integrations/supabase/client";
import { Theme, isValidTheme } from "./types";

export class ThemeLoader {
  private static themeChangeCallback: ((theme: Theme) => void) | null = null;
  private static isInitialized = false;

  static setThemeChangeCallback(callback: (theme: Theme) => void) {
    this.themeChangeCallback = callback;
  }

  static async loadUserTheme(userId: string) {
    try {
      console.log("Loading theme for user:", userId);
      
      // First try to get theme from user-specific localStorage (fastest)
      const userTheme = this.getUserThemeFromStorage(userId);
      if (userTheme) {
        console.log("Found theme in localStorage:", userTheme);
        this.applyThemeToDOM(userTheme);
        if (this.themeChangeCallback) {
          this.themeChangeCallback(userTheme);
        }
        this.isInitialized = true;
        
        // Sync with database in background
        this.syncThemeWithDatabase(userId, userTheme);
        return;
      }

      // If not in localStorage, load from database
      const { data: settings } = await supabase
        .from("user_settings")
        .select("theme")
        .eq("user_id", userId)
        .maybeSingle();
      
      let theme: Theme;
      
      if (settings?.theme) {
        theme = settings.theme as Theme;
        console.log("Loaded theme from database:", theme);
      } else {
        // Default theme for new users
        theme = "light";
        console.log("Using default theme:", theme);
      }
      
      // Save to localStorage immediately
      this.saveThemeToStorage(userId, theme);
      
      // Apply theme to DOM
      this.applyThemeToDOM(theme);
      
      // Notify useTheme hook of the change
      if (this.themeChangeCallback) {
        this.themeChangeCallback(theme);
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error("Error loading theme:", error);
      // Fallback to light theme
      this.applyThemeToDOM("light");
      if (this.themeChangeCallback) {
        this.themeChangeCallback("light");
      }
      this.isInitialized = true;
    }
  }

  static async saveTheme(userId: string, theme: Theme) {
    try {
      console.log("Saving theme:", theme, "for user:", userId);
      
      // Save to localStorage immediately (synchronous)
      this.saveThemeToStorage(userId, theme);
      
      // Apply to DOM immediately
      this.applyThemeToDOM(theme);
      
      // Save to database in background
      await this.syncThemeWithDatabase(userId, theme);
      
    } catch (error) {
      console.error("Error saving theme:", error);
    }
  }

  private static async syncThemeWithDatabase(userId: string, theme: Theme) {
    try {
      await supabase
        .from("user_settings")
        .upsert({ 
          user_id: userId, 
          theme 
        }, { 
          onConflict: "user_id" 
        });
      console.log("Theme synced with database:", theme);
    } catch (error) {
      console.error("Error syncing theme with database:", error);
    }
  }

  private static saveThemeToStorage(userId: string, theme: Theme) {
    localStorage.setItem(`theme_${userId}`, theme);
    localStorage.setItem("theme", theme); // Keep for compatibility
  }

  static clearUserTheme() {
    // Remove all theme-related localStorage keys
    const keysToRemove = Object.keys(localStorage).filter(key => 
      key.startsWith("theme_") || key === "theme"
    );
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Remove theme classes from DOM
    document.documentElement.classList.remove("dark", "ocean", "sunset", "forest");
    
    // Apply default light theme
    this.applyThemeToDOM("light");
    
    // Notify useTheme hook of the change
    if (this.themeChangeCallback) {
      this.themeChangeCallback("light");
    }
    
    this.isInitialized = false;
  }

  static getUserThemeFromStorage(userId: string): Theme | null {
    const userTheme = localStorage.getItem(`theme_${userId}`);
    if (userTheme && isValidTheme(userTheme)) {
      return userTheme as Theme;
    }
    
    // Fallback to generic theme key
    const genericTheme = localStorage.getItem("theme");
    if (genericTheme && isValidTheme(genericTheme)) {
      return genericTheme as Theme;
    }
    
    return null;
  }

  static getIsInitialized(): boolean {
    return this.isInitialized;
  }

  static applyThemeToDOM(theme: Theme) {
    console.log("Applying theme to DOM:", theme);
    
    // Remove any existing theme classes
    document.documentElement.classList.remove("dark", "ocean", "sunset", "forest");
    
    // Apply the correct theme class
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else if (theme === "ocean") {
      document.documentElement.classList.add("ocean");
    } else if (theme === "sunset") {
      document.documentElement.classList.add("sunset");
    } else if (theme === "forest") {
      document.documentElement.classList.add("forest");
    } else if (theme === "system") {
      // For system preference
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      document.documentElement.classList.toggle("dark", prefersDark);
    }
    
    console.log("DOM classes after theme application:", document.documentElement.classList.toString());
  }
}
